import ExcelJS from "exceljs";
import {
  downloadWorkbook,
  getCompanyName,
  styleLightHeader,
  applyDataBorder,
  addTitleBlock,
  applyHeaderBasedColumnWidths,
  REPORT_COLORS,
  moneyFmt,
} from "./payrollReportExcelShared";
import { formatInr } from "./runPayrollUtils";

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function netFromComponents(components = []) {
  return round2(
    (components || []).reduce((sum, c) => {
      if (!c || c.included === false) return sum;
      const amt = Number(c.finalAmount != null ? c.finalAmount : c.systemAmount) || 0;
      return c.type === "deduction" ? sum - amt : sum + amt;
    }, 0)
  );
}

/** Settlement Statement for one F&F. */
export async function exportFnfSettlementExcel({
  employee = {},
  settlement = {},
  payMonth = "",
} = {}) {
  const components = Array.isArray(settlement.componentsJson)
    ? settlement.componentsJson
    : [];
  const included = components.filter((c) => c && c.included !== false);
  const headers = [
    "Sl. No",
    "Section",
    "Component",
    "Type",
    "System",
    "Final",
    "Remarks",
  ];
  const wb = new ExcelJS.Workbook();
  wb.creator = "PaceHRM";
  wb.created = new Date();
  const ws = wb.addWorksheet("Settlement Statement", {
    views: [{ state: "frozen", ySplit: 6 }],
  });

  const name = employee.name || "Employee";
  const code = employee.empCode || "";
  const lwd = settlement.lastWorkingDay || employee.lastWorkingDay || "—";
  const method =
    settlement.processMethodLabel ||
    settlement.processMethod ||
    (settlement.includeLeavingSalary ? "With salary" : "Settlement only");

  addTitleBlock(
    ws,
    getCompanyName(),
    `Full & Final Settlement Statement — ${code} ${name}`.trim(),
    `Pay month ${payMonth || settlement.payMonth || "—"}  ·  LWD ${lwd}  ·  Method ${method}  ·  Status ${settlement.status || "draft"}  ·  ${new Date().toLocaleString("en-IN")}`,
    headers.length
  );

  const head = ws.addRow(headers);
  head.height = 22;
  head.eachCell((cell) => styleLightHeader(cell, REPORT_COLORS.summary));

  included.forEach((c, i) => {
    const row = ws.addRow([
      i + 1,
      c.section === "salary" ? "Leaving-month salary" : "Settlement",
      c.label || c.key,
      c.type === "deduction" ? "Deduction" : "Earning",
      round2(c.systemAmount),
      round2(c.finalAmount != null ? c.finalAmount : c.systemAmount),
      c.remarks || "",
    ]);
    row.eachCell((cell, col) => {
      applyDataBorder(cell);
      cell.font = {
        size: 10,
        color: { argb: c.type === "deduction" ? "FFDC2626" : "FF111827" },
        bold: col === 3,
      };
      if (col === 5 || col === 6) moneyFmt(cell);
      cell.alignment = {
        vertical: "middle",
        horizontal: col === 5 || col === 6 ? "right" : "left",
        wrapText: col === 7,
      };
    });
  });

  ws.addRow([]);
  const net = netFromComponents(included);
  const netRow = ws.addRow(["", "", "Net F&F", "", "", net, ""]);
  netRow.eachCell((cell, col) => {
    cell.font = { bold: true, size: 11, color: { argb: "FF1E3A5F" } };
    if (col === 6) moneyFmt(cell);
  });

  applyHeaderBasedColumnWidths(ws, headers, { min: 10, max: 40 });
  ws.getColumn(7).width = 42;

  const safe = `${code || "emp"}_${String(payMonth || "month").replace(/[^\w.-]+/g, "_")}`;
  return downloadWorkbook(wb, `FnF_Settlement_${safe}.xlsx`);
}

export { netFromComponents, formatInr };
